import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Box, Cyl, Ball } from "./Box";

const GOLD = "#D9A441";

const Stool = () => (
  <group>
    <Cyl p={[0, 0.5, 0]} a={[0.26, 0.26, 0.06, 32]} c="#8A5A35" r={0.5} t="wood" />
    {[0, 1, 2].map((k) => {
      const a = (k / 3) * Math.PI * 2;
      return <Cyl key={k} p={[Math.cos(a) * 0.15, 0.24, Math.sin(a) * 0.15]} rot={[Math.sin(a) * 0.18, 0, -Math.cos(a) * 0.18]} a={[0.02, 0.025, 0.5, 8]} c="#5C3A21" t="wood" />;
    })}
  </group>
);

const Glow = ({ y = 0.8, color = "#FFC266" }) => <pointLight position={[0, y, 0.2]} color={color} intensity={1.2} distance={2.2} decay={2} />;

const Notebook = () => (
  <group position={[0, 0.53, 0]} rotation={[0, 0.4, 0]}>
    <Box p={[0, 0.025, 0]} s={[0.32, 0.05, 0.24]} c="#3E5C76" t="leather" round={0.01} />
    <Box p={[0.005, 0.026, 0]} s={[0.31, 0.04, 0.225]} c="#F6EEDC" t="paper" />
    <Box p={[0.12, 0.051, 0]} s={[0.012, 0.002, 0.24]} c="#C85A32" shadow={false} />
    <Cyl p={[-0.05, 0.06, 0.15]} rot={[0, 0, Math.PI / 2]} a={[0.008, 0.008, 0.2, 8]} c="#2B2A2E" />
  </group>
);

const Keepsake = () => (
  <group position={[0, 0.53, 0]} rotation={[0, -0.3, 0]}>
    <Box p={[0, 0.09, 0]} s={[0.36, 0.18, 0.26]} c="#C9A27A" t="paper" round={0.01} />
    <Box p={[0, 0.2, 0]} s={[0.38, 0.05, 0.28]} c="#B88E66" t="paper" round={0.01} />
    <Box p={[0, 0.2, 0]} s={[0.05, 0.052, 0.285]} c="#C85A32" t="fabric" />
  </group>
);

const Easel = () => (
  <group>
    <Box p={[-0.22, 0.75, 0]} rot={[0.12, 0, 0.12]} s={[0.04, 1.5, 0.04]} c="#8A5A35" t="wood" />
    <Box p={[0.22, 0.75, 0]} rot={[0.12, 0, -0.12]} s={[0.04, 1.5, 0.04]} c="#8A5A35" t="wood" />
    <Box p={[0, 0.7, -0.3]} rot={[-0.35, 0, 0]} s={[0.04, 1.45, 0.04]} c="#8A5A35" t="wood" />
    <Box p={[0, 0.7, 0.08]} s={[0.6, 0.04, 0.08]} c="#8A5A35" t="wood" />
    <Box p={[0, 1.04, 0.08]} rot={[-0.12, 0, 0]} s={[0.58, 0.62, 0.04]} c="#B8862E" m={0.4} r={0.4} />
    <Box p={[0, 1.04, 0.105]} rot={[-0.12, 0, 0]} s={[0.5, 0.54, 0.01]} c="#F2E6D3" t="canvas" />
  </group>
);

const Lantern = () => (
  <group position={[0, 0.53, 0]}>
    <Box p={[0, 0.02, 0]} s={[0.2, 0.04, 0.2]} c="#2B2A2E" m={0.6} r={0.4} />
    <Box p={[0, 0.17, 0]} s={[0.16, 0.26, 0.16]} c="#FFE2B0" e="#FFB852" ei={1.6} />
    {[[1, 1], [1, -1], [-1, 1], [-1, -1]].map(([x, z]) => <Box key={`${x}${z}`} p={[x * 0.085, 0.17, z * 0.085]} s={[0.02, 0.28, 0.02]} c="#2B2A2E" m={0.6} />)}
    <mesh position={[0, 0.36, 0]}><coneGeometry args={[0.14, 0.1, 4]} /><meshStandardMaterial color="#2B2A2E" metalness={0.6} roughness={0.4} /></mesh>
    <Glow y={0.2} />
  </group>
);

const Cup = () => (
  <group position={[0, 0.53, 0]}>
    <Box p={[0, 0.05, 0]} s={[0.16, 0.1, 0.16]} c="#3D2415" t="wood" />
    <Cyl p={[0, 0.16, 0]} a={[0.025, 0.035, 0.12, 12]} c={GOLD} m={0.85} r={0.25} />
    <Cyl p={[0, 0.3, 0]} a={[0.11, 0.05, 0.18, 24]} c={GOLD} m={0.85} r={0.25} />
  </group>
);

const StarLight = () => {
  const ref = useRef();
  useFrame((_, dt) => { if (ref.current) ref.current.rotation.y += dt * 0.6; });
  return (
    <group position={[0, 0.53, 0]}>
      <Cyl p={[0, 0.02, 0]} a={[0.08, 0.1, 0.04, 20]} c="#2B2A2E" />
      <group ref={ref} position={[0, 0.25, 0]}>
        <mesh><octahedronGeometry args={[0.13, 0]} /><meshStandardMaterial color="#FFE2B0" emissive="#FFB347" emissiveIntensity={1.4} roughness={0.2} /></mesh>
      </group>
      <Glow y={0.25} />
    </group>
  );
};

const Quill = () => (
  <group position={[0, 0.53, 0]}>
    <Box p={[0, 0.006, 0]} s={[0.3, 0.012, 0.22]} c="#F6EEDC" t="paper" />
    <Cyl p={[0.08, 0.05, 0.03]} a={[0.04, 0.045, 0.08, 16]} c="#1E1E22" r={0.2} />
    <mesh position={[0.06, 0.2, 0.03]} rotation={[0, 0, 0.35]} scale={[0.25, 1, 0.05]} castShadow>
      <sphereGeometry args={[0.14, 12, 10]} />
      <meshStandardMaterial color="#F4EEE2" roughness={0.8} />
    </mesh>
  </group>
);

const Trunk = () => (
  <group rotation={[0, 0.3, 0]}>
    <Box p={[0, 0.2, 0]} s={[0.6, 0.4, 0.38]} c="#6E4428" t="leather" round={0.02} />
    <Ball p={[0, 0.4, 0]} rad={0.3} c="#6E4428" sc={[1, 0.3, 0.62]} t="leather" />
    {[-0.2, 0.2].map((x) => <Box key={x} p={[x, 0.25, 0]} s={[0.04, 0.42, 0.39]} c={GOLD} m={0.7} r={0.35} />)}
    <Box p={[0, 0.32, 0.195]} s={[0.07, 0.08, 0.02]} c={GOLD} m={0.8} />
  </group>
);

const MODELS_3D = { notebook: Notebook, box: Keepsake, lamp: Lantern, trophy: Cup, star: StarLight, quill: Quill };

export const CustomModel = ({ model }) => {
  if (model === "frame") return <Easel />;
  if (model === "chest") return <Trunk />;
  const Item = MODELS_3D[model] || Notebook;
  return <group><Stool /><Item /></group>;
};
